// api.ts
// Real backend API service with runtime configuration support
import { API_URL } from '../utils/config';

// Use the API URL from runtime configuration
const API_BASE = API_URL;

// Helper: Get JWT from localStorage (or however you store it)
function getToken() {
  return localStorage.getItem('access_token');
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}



export const api = {
  auth: {
    signIn: async (email: string, password: string) => {
      try {
        const res = await fetch(`${API_BASE}/auth/signin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error('Sign-in error response:', errorText);
          return { data: null, error: { message: errorText || 'Invalid login credentials' } };
        }
        
        const data = await res.json();
        // Store token for future requests
        if (data.token) {
          localStorage.setItem('access_token', data.token);
        }
        return { data, error: null };
      } catch (err) {
        console.error('Sign-in exception:', err);
        return { data: null, error: { message: err instanceof Error ? err.message : 'Failed to connect to authentication service' } };
      }
    },
    signUp: async (email: string, password: string, full_name: string, farm_name: string) => {
      const res = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, full_name, farm_name })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('access_token', data.token);
      }
      return data;
    },
    signOut: async () => {
      try {
        // First make the API call to signout on the server
        const res = await fetch(`${API_BASE}/auth/signout`, {
          method: 'POST',
          headers: { ...authHeaders() }
        });
        
        // Then remove the token from localStorage
        localStorage.removeItem('access_token');
        
        if (!res.ok) {
          const errorText = await res.text();
          return { error: errorText };
        }
        
        return { data: await res.json(), error: undefined };
      } catch (err) {
        console.error('Signout error:', err);
        // Still remove the token even if the API call fails
        localStorage.removeItem('access_token');
        return { error: err instanceof Error ? err.message : 'Unknown error during signout' };
      }
    },
    getSession: async () => {
      const token = getToken();
      if (!token) return { error: 'No session token found' };
      
      try {
        const res = await fetch(`${API_BASE}/auth/session`, {
          method: 'GET',
          headers: { ...authHeaders() }
        });
        if (!res.ok) throw new Error(await res.text());
        return { data: await res.json() };
      } catch (err) {
        return { error: err instanceof Error ? err.message : 'Failed to verify session' };
      }
    },
    getUser: async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/user`, {
          headers: { ...authHeaders() }
        });
        if (!res.ok) throw new Error(await res.text());
        return await res.json();
      } catch (err) {
        throw err;
      }
    },
    resetPassword: async (email: string) => {
      try {
        const res = await fetch(`${API_BASE}/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          return { data: null, error: errorText };
        }
        
        const data = await res.json();
        return { data, error: null };
      } catch (err) {
        return { data: null, error: err instanceof Error ? err.message : 'Failed to request password reset' };
      }
    },
    updatePassword: async (token: string, newPassword: string) => {
      try {
        const res = await fetch(`${API_BASE}/auth/update-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, password: newPassword })
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          return { data: null, error: errorText };
        }
        
        const data = await res.json();
        return { data, error: null };
      } catch (err) {
        return { data: null, error: err instanceof Error ? err.message : 'Failed to update password' };
      }
    }
  },
  farms: {
    getAll: async () => {
      try {
        const res = await fetch(`${API_BASE}/farms`, { headers: { ...authHeaders() } });
        if (!res.ok) {
          return { data: undefined, error: await res.text() };
        }
        const data = await res.json();
        return { data, error: undefined };
      } catch (err: any) {
        return { data: undefined, error: err?.message || 'Unknown error' };
      }
    },
    getById: async (id: string) => {
      const res = await fetch(`${API_BASE}/farms/${id}`, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
    create: async (name: string) => {
      try {
        const res = await fetch(`${API_BASE}/farms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ name })
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          return { data: undefined, error: errorText };
        }
        
        const data = await res.json();
        return { data, error: undefined };
      } catch (err: any) {
        return { data: undefined, error: err?.message || 'Unknown error' };
      }
    },
    update: async (id: string, name: string) => {
      try {
        const res = await fetch(`${API_BASE}/farms/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ name })
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          return { data: undefined, error: errorText };
        }
        
        const data = await res.json();
        return { data, error: undefined };
      } catch (err: any) {
        return { data: undefined, error: err?.message || 'Unknown error' };
      }
    },
    delete: async (id: string) => {
      const res = await fetch(`${API_BASE}/farms/${id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
  },
  zones: {
    getAll: async (farmId: string) => {
      try {
        const res = await fetch(`${API_BASE}/zones?farm_id=${farmId}`, { headers: { ...authHeaders() } });
        if (!res.ok) {
          return { data: undefined, error: await res.text() };
        }
        const data = await res.json();
        return { data, error: undefined };
      } catch (err: any) {
        return { data: undefined, error: err?.message || 'Unknown error' };
      }
    },
    getById: async (id: string) => {
      const res = await fetch(`${API_BASE}/zones/${id}`, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
    create: async (name: string, farmId: string) => {
      try {
        const res = await fetch(`${API_BASE}/zones`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ name, farm_id: farmId })
        });
        if (!res.ok) {
          return { data: undefined, error: await res.text() };
        }
        const data = await res.json();
        return { data, error: undefined };
      } catch (err: any) {
        return { data: undefined, error: err?.message || 'Unknown error' };
      }
    },
    update: async (id: string, name: string) => {
      const res = await fetch(`${API_BASE}/zones/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name })
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
    delete: async (id: string) => {
      const res = await fetch(`${API_BASE}/zones/${id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
  },
  measurements: {
    getAll: async (zoneId: string) => {
      const res = await fetch(`${API_BASE}/measurements?zone_id=${zoneId}`, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
    getById: async (id: string) => {
      const res = await fetch(`${API_BASE}/measurements/${id}`, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
    create: async (measurement: any) => {
      const res = await fetch(`${API_BASE}/measurements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(measurement)
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
    update: async (id: string, measurement: any) => {
      const res = await fetch(`${API_BASE}/measurements/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(measurement)
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
    delete: async (id: string) => {
      const res = await fetch(`${API_BASE}/measurements/${id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
  }
};
